import { z } from 'zod';

import { meProfileSchema } from './onboarding';

/** Display name when Apple hides the name (empty string in DB). */
export function fallbackUserDisplayName(email: string): string {
  const local = email.split('@')[0]?.trim();
  if (local && local.length > 0) return local.slice(0, 120);
  return 'User';
}

/**
 * Wire shape for auth user on `GET /v1/me`.
 * Apple Sign in with Apple often stores `name: ""` and `image: null` — empty name
 * must not fail client Zod (that previously surfaced as “Could not load profile”).
 */
export const userSchema = z
  .object({
    id: z.string().min(1),
    email: z.string().email(),
    name: z.string().max(120),
    emailVerified: z.boolean(),
    image: z.union([z.string().url(), z.literal(''), z.null()]),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .transform((user) => ({
    ...user,
    name: user.name.trim() || fallbackUserDisplayName(user.email),
    image: user.image ? user.image : null,
  }));

export const createUserInputSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(120),
});

export type CreateUserInput = z.infer<typeof createUserInputSchema>;

export const authProviderSchema = z.enum(['google', 'apple']);
export type AuthProviderInput = z.infer<typeof authProviderSchema>;

export const meResponseSchema = z.object({
  user: userSchema,
  session: z.object({
    id: z.string().min(1),
    expiresAt: z.string().datetime(),
  }),
  profile: meProfileSchema.nullable(),
});

export type MeResponse = z.infer<typeof meResponseSchema>;
export type MeUser = z.infer<typeof userSchema>;
